import { parseCallLink, type CallSessionLink } from './deepLink.ts';

export const CALL_LINK_LIFETIME = 5 * 60_000;
const PENDING = 'sidetone.call-link.pending.v1';
const CONSUMED = 'sidetone.call-link.consumed.v1';
const SESSION_WINDOW_RE = /^sidetone-session-([0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12})$/i;
export type CallIntent = { e164: string; created: number; account: string | null; session?: CallSessionLink };
export type LaunchSnapshot = { pending: CallIntent | null; review: CallIntent | null; message: string | null; revision: number };
type Store = Pick<Storage, 'getItem' | 'setItem' | 'removeItem'>;
const read = (store: Store | null, key: string): unknown => { try { return JSON.parse(store?.getItem(key) ?? 'null'); } catch { return null; } };
const write = (store: Store | null, key: string, value: unknown) => { try { if (value === null) store?.removeItem(key); else store?.setItem(key, JSON.stringify(value)); } catch { /* Memory remains authoritative in this window. */ } };
const intentKey = (intent: Pick<CallIntent, 'e164' | 'session'>) => intent.session ? `attempt:${intent.session.attemptId}` : intent.e164;

/** A manual link is a short-lived review request; a session link carries a reserved calling-session attempt. Consumed before delivery. */
export class CallLaunchIntents {
  private snapshot: LaunchSnapshot = { pending: null, review: null, message: null, revision: 0 };
  private listeners = new Set<() => void>();
  private consumed: Record<string, number> = {};
  private account: string | null = null;
  constructor(private options: { origin: string; session?: Store | null; shared?: Store | null; now?: () => number }) {
    const saved = read(options.session ?? null, PENDING) as CallIntent | null;
    if (saved && typeof saved.e164 === 'string' && typeof saved.created === 'number' && (saved.account === null || typeof saved.account === 'string') && this.fresh(saved.created)) {
      const savedUrl = saved.session
        ? `${options.origin}/call?number=${encodeURIComponent(saved.e164)}&attempt_id=${encodeURIComponent(saved.session.attemptId)}&candidate_id=${saved.session.candidateId}${saved.session.controllerId ? `&controller_id=${encodeURIComponent(saved.session.controllerId)}` : ''}`
        : `${options.origin}/call?number=${encodeURIComponent(saved.e164)}`;
      const check = parseCallLink(savedUrl, options.origin);
      if (check.ok && (!saved.session || check.session)) this.snapshot = { ...this.snapshot, pending: saved };
      else write(options.session ?? null, PENDING, null);
    } else write(options.session ?? null, PENDING, null);
  }
  private now() { return this.options.now?.() ?? Date.now(); }
  private fresh(time: number) { return Number.isFinite(time) && time <= this.now() && this.now() - time < CALL_LINK_LIFETIME; }
  getSnapshot = () => this.snapshot;
  subscribe = (listener: () => void) => { this.listeners.add(listener); return () => { this.listeners.delete(listener); }; };
  private set(pending: CallIntent | null, message: string | null, review: CallIntent | null = null) {
    this.snapshot = { pending, review, message, revision: this.snapshot.revision + 1 };
    write(this.options.session ?? null, PENDING, pending);
    for (const listener of this.listeners) listener();
  }
  private wasConsumed(key: string, sharedWindowMs = CALL_LINK_LIFETIME) {
    // Window-session tombstones survive refresh and Back, including beyond the intent's lifetime.
    const local = read(this.options.session ?? null, CONSUMED);
    if (local && typeof local === 'object' && !Array.isArray(local)) {
      for (const [k, value] of Object.entries(local)) if (typeof value === 'number' && Number.isFinite(value)) this.consumed[k] = value;
    }
    const saved = read(this.options.shared ?? null, CONSUMED);
    if (saved && typeof saved === 'object' && !Array.isArray(saved)) {
      for (const [k, value] of Object.entries(saved)) if (typeof value === 'number' && this.fresh(value) && this.now() - value < sharedWindowMs) this.consumed[k] = value;
    }
    this.consumed = Object.fromEntries(Object.entries(this.consumed).slice(-1000));
    return this.consumed[key] !== undefined;
  }
  private remember(key: string) {
    this.wasConsumed(key);
    this.consumed[key] = this.now();
    write(this.options.session ?? null, CONSUMED, this.consumed);
    write(this.options.shared ?? null, CONSUMED, Object.fromEntries(Object.entries(this.consumed).filter(([, at]) => this.fresh(at)).slice(-100)));
  }
  capture(raw: string, windowName?: string): boolean {
    const result = parseCallLink(raw, this.options.origin);
    if (!result.ok) { this.cancel(result.message); return false; }
    let session = result.session;
    if (session && !session.controllerId) {
      const candidateName = windowName ?? (typeof window !== 'undefined' ? window.name : '');
      const match = SESSION_WINDOW_RE.exec(candidateName ?? '');
      if (match?.[1]) session = { ...session, controllerId: match[1].toLowerCase() };
    }
    const key = intentKey({ e164: result.e164, ...(session ? { session } : {}) });
    if (this.wasConsumed(key, 2000)) {
      // A late launchQueue delivery can follow navigation after review has mounted.
      // Coalesce it with that review; never erase the user's explicit line choice.
      const review = this.snapshot.review && intentKey(this.snapshot.review) === key ? this.snapshot.review : null;
      this.set(null, review ? (review.session ? 'Starting calling-session call in the phone…' : 'Review the number and choose your outgoing line in the phone, then press Call.') : 'This call link has already been reviewed or dismissed. To call again, enter the number in the phone.', review);
      return false;
    }
    const pending = this.snapshot.pending;
    if (pending && intentKey(pending) === key) {
      if (this.fresh(pending.created)) return true;
      this.cancel('This call link expired. Enter the number in the phone to call.');
      return false;
    }
    if (pending) this.remember(intentKey(pending)); // A newer explicit request replaces the unreviewed one.
    this.set({ e164: result.e164, created: this.now(), account: this.account, ...(session ? { session } : {}) }, null);
    return true;
  }
  bindAccount(account: string | null) {
    const pending = this.snapshot.pending ?? this.snapshot.review;
    if ((this.account && this.account !== account) || (pending?.account && pending.account !== account)) this.cancel('The call link was cleared because the account or workspace changed.');
    this.account = account;
    if (account && this.snapshot.pending && !this.snapshot.pending.account) this.set({ ...this.snapshot.pending, account }, null);
  }
  clear() { this.cancel('The call link was cleared on sign-out.'); this.account = null; }
  cancel(message = 'Call link dismissed. No call was placed.') {
    if (this.snapshot.pending) this.remember(intentKey(this.snapshot.pending));
    this.set(null, message);
  }
  /** Called only after authenticated workspace data is ready, including while busy or observing. */
  deliver(busy: boolean, owner: boolean): CallIntent | null {
    const pending = this.snapshot.pending;
    if (!pending) return null;
    if (!this.fresh(pending.created)) { this.cancel('This call link expired. Open a new link when you are ready.'); return null; }
    if (this.wasConsumed(intentKey(pending))) { this.set(null, 'This call link has already been reviewed in another window.'); return null; }
    if (busy) { this.cancel('Your phone is busy starting, ringing or on a call. Finish it, then enter the number in the phone. No call was queued.'); return null; }
    if (!owner || !this.account) return null;
    this.remember(intentKey(pending));
    this.set(null, pending.session ? 'Starting calling-session call in the phone…' : 'Review the number and choose your outgoing line in the phone, then press Call.', pending);
    return pending;
  }
}

let current: CallLaunchIntents | null = null;
export function callLaunchIntents(): CallLaunchIntents {
  if (!current) {
    const storage = (kind: 'sessionStorage' | 'localStorage') => { try { return typeof window === 'undefined' ? null : window[kind]; } catch { return null; } };
    current = new CallLaunchIntents({ origin: typeof window === 'undefined' ? 'https://sidet.one' : window.location.origin, session: storage('sessionStorage'), shared: storage('localStorage') });
  }
  return current;
}

/** main.tsx calls this before rendering/sign-in. focus-existing delivers URLs without navigation. */
export function captureCallLaunches(target: Window = window, intents = callLaunchIntents()) {
  const capture = (raw: string) => {
    // Other app launches do not clear a pending phone request.
    try { if (new URL(raw).pathname !== '/call') return; } catch { return; }
    intents.capture(raw, (target as { name?: string }).name);
    // No query is left in this window's history to replay on refresh or OAuth return.
    if (target.location.pathname === '/call') target.history.replaceState(null, '', '/call');
    target.dispatchEvent(new Event('sidetone-call-launch'));
  };
  if (target.location.pathname === '/call' && target.location.search) capture(target.location.href);
  const queue = (target as Window & { launchQueue?: { setConsumer: (consumer: (params: { targetURL?: string }) => void) => void } }).launchQueue;
  queue?.setConsumer(params => { if (params.targetURL) capture(params.targetURL); });
  const pop = () => { if (target.location.pathname === '/call' && target.location.search) capture(target.location.href); };
  target.addEventListener('popstate', pop);
  return () => target.removeEventListener('popstate', pop);
}
